import { AppIcon } from "@capacitor-community/app-icon";

import { Models } from "api/types";
import { isNativeMobileApp } from "utils/platform.helpers";

export type LauncherKey = `launcher_${string}`;

export const changeAppIcon = async (
  iconName: LauncherKey,
  iconsToDisable: LauncherKey[],
): Promise<boolean> => {
  if (!isNativeMobileApp() || !iconName) {
    return false;
  }

  const isSupported = await AppIcon.isSupported();

  if (!isSupported.value) {
    return false;
  }

  try {
    await AppIcon.change({
      name: iconName,
      suppressNotification: true,
      disable: iconsToDisable, // required on Android
    });

    return true;
  } catch (error) {
    console.error("[RMD]: Failed to change app icon", error);
    return false;
  }
};

export const resetAppIcon = async (iconsToDisable: LauncherKey[]): Promise<boolean> => {
  if (!isNativeMobileApp()) {
    return false;
  }

  try {
    await AppIcon.reset({
      suppressNotification: true,
      disable: iconsToDisable, // required on Android
    });

    return true;
  } catch (error) {
    console.error("[RMD]: Failed to reset to app icon", error);
    return false;
  }
};

export const convertSubDomainToLauncherIcon = (subDomain?: string): LauncherKey | "" => {
  if (!subDomain) {
    return "";
  }

  const sanitized = sanitizeIconName(subDomain);
  return `launcher_${sanitized}`;
};

export const getLauncherIconsFromPlatformDomainTokens = (
  platformDomainTokens?: Models<"GetMultiPlatformUserPlatformsResults">["platformDomainTokens"],
): LauncherKey[] => {
  return (
    platformDomainTokens?.map(({ platformSubdomain }) => {
      return convertSubDomainToLauncherIcon(platformSubdomain) as LauncherKey;
    }) || []
  );
};

/**
 * This function's logic is supposed to exactly match the logic in `sanitize_filename`.
 * @see {@link file://./../../scripts/mobile-app-icons/download_platform_icons.py}
 */
const sanitizeIconName = (rawIconName: string) =>
  rawIconName
    .toLowerCase()
    .replace(/\./g, "_")
    .replace(/[^a-z0-9_]/g, "");
